import { Button, cn, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, getPageHeader, PageContainer, PageHeader, PageHeaderActionPrimary, PageHeaderActions, PageHeaderLeading, PageHeaderStack, Skeleton } from '@yeshuman/sdk'
import { ClipboardList, Loader2, Plus } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'


import { CANVAS_TYPE_OPTIONS } from './canvas-types'
import { useCanvases, useCreateCanvas, type CanvasListItem, type CanvasTypeId } from './use-canvases'

function formatDate(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString(undefined, { dateStyle: 'medium' })
}

function CanvasRow({ canvas, onOpen }: { canvas: CanvasListItem; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'flex w-full items-center gap-3 py-3 text-left',
        'transition-colors hover:bg-muted/50',
      )}
    >
      <ClipboardList className="size-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
        {canvas.title.trim() || 'Untitled canvas'}
      </span>
      <span className="shrink-0 text-xs text-muted-foreground">
        {canvas.type_label}
        {canvas.stub ? ' · Stub' : ''}
        {' · '}
        {formatDate(canvas.updated_at)}
      </span>
    </button>
  )
}

export function CanvasesPage() {
  const navigate = useNavigate()
  const header = getPageHeader(
    '/canvases',
    'Canvases',
    'Workshop documents: business model, lean, and strategy canvases.',
  )
  const { data: canvases = [], isLoading, isError, refetch } = useCanvases()
  const createCanvas = useCreateCanvas()
  const [pickerOpen, setPickerOpen] = useState(false)

  async function handleCreate(canvasType: CanvasTypeId) {
    const canvas = await createCanvas.mutateAsync({ canvas_type: canvasType })
    setPickerOpen(false)
    navigate(`/canvases/${canvas.id}`)
  }

  return (
    <PageContainer>
      <PageHeaderStack>
        <PageHeader
          title={header.title}
          description={header.description}
          leading={<PageHeaderLeading />}
          actions={
            <PageHeaderActions
              primary={
                <PageHeaderActionPrimary onClick={() => setPickerOpen(true)} disabled={createCanvas.isPending}>
                  {createCanvas.isPending ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
                  New canvas
                </PageHeaderActionPrimary>
              }
            />
          }
        />
      </PageHeaderStack>

      {isLoading ? (
        <div className="flex flex-col gap-3 py-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <p className="text-sm text-muted-foreground">Could not load canvases.</p>
          <Button variant="outline" onClick={() => void refetch()}>
            Try again
          </Button>
        </div>
      ) : canvases.length === 0 ? (
        <div className="flex flex-col items-center gap-4 py-16 text-center">
          <div className="flex size-12 items-center justify-center rounded-full bg-muted">
            <ClipboardList className="size-6 text-muted-foreground" />
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium text-foreground">No canvases yet</p>
            <p className="max-w-sm text-sm text-muted-foreground">
              Start a business model canvas, or pick a lean or strategy stub to capture notes.
            </p>
          </div>
          <Button onClick={() => setPickerOpen(true)} disabled={createCanvas.isPending}>
            <Plus data-icon="inline-start" />
            New canvas
          </Button>
        </div>
      ) : (
        <div className="divide-y divide-border">
          {canvases.map((canvas) => (
            <CanvasRow
              key={canvas.id}
              canvas={canvas}
              onOpen={() => navigate(`/canvases/${canvas.id}`)}
            />
          ))}
        </div>
      )}

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New canvas</DialogTitle>
            <DialogDescription>Choose a workshop document type.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            {CANVAS_TYPE_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                disabled={createCanvas.isPending}
                onClick={() => void handleCreate(option.id)}
                className="rounded-md border border-border px-3 py-3 text-left hover:bg-muted/50 disabled:opacity-50"
              >
                <span className="block text-sm font-medium text-foreground">{option.label}</span>
                <span className="mt-1 block text-xs text-muted-foreground">{option.description}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </PageContainer>
  )
}
